import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, Card, Badge, Button } from '@agion/ui'
import { RefreshCw, Wifi, WifiOff, CheckCircle2, Clipboard, Check } from 'lucide-react'
import { useOperationLogs } from '@/hooks/infra_use_operation_logs'
import type { OperationLogLine, OperationStatus } from '@/core/infra_types'

interface OperationLogViewerProps {
  operationId: string
  enabled?: boolean
  onLogsChange?: (logs: OperationLogLine[]) => void
  onTerminalStatus?: (status: OperationStatus) => void
}

const levelColorMap: Record<OperationLogLine['level'], string> = {
  info: 'text-slate-300',
  warn: 'text-yellow-300',
  error: 'text-red-400',
  debug: 'text-slate-500',
}

const levelLabelMap: Record<OperationLogLine['level'], { variant: 'default' | 'warning' | 'error' | 'info'; show: boolean }> = {
  info: { variant: 'info', show: false },
  warn: { variant: 'warning', show: true },
  error: { variant: 'error', show: true },
  debug: { variant: 'default', show: false },
}

export function OperationLogViewer({ operationId, enabled = true, onLogsChange, onTerminalStatus }: OperationLogViewerProps) {
  const { t } = useTranslation(['pages'])
  const { logs, connectionState, terminalStatus, error, reconnect } = useOperationLogs({
    operationId,
    enabled,
  })
  const scrollRef = useRef<HTMLDivElement>(null)
  const notifiedRef = useRef<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    const text = logs
      .map((l) => `[${new Date(l.timestamp).toLocaleTimeString()}] [${l.level.toUpperCase()}] ${l.message}`)
      .join('\n')
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [logs])

  // Notify parent of log changes (for progress stepper)
  useEffect(() => {
    onLogsChange?.(logs)
  }, [logs, onLogsChange])

  // Notify parent when terminal status arrives (once)
  useEffect(() => {
    if (terminalStatus && notifiedRef.current !== terminalStatus) {
      notifiedRef.current = terminalStatus
      onTerminalStatus?.(terminalStatus)
    }
  }, [terminalStatus, onTerminalStatus])

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [logs])

  return (
    <Card>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t('pages:operations.logViewer.title', 'Operation Logs')}
            </h3>
            {connectionState === 'streaming' && (
              <Badge variant="success">
                <Wifi size={12} className="mr-1" />
                {t('pages:operations.logViewer.connected', 'Live')}
              </Badge>
            )}
            {connectionState === 'completed' && (
              <Badge variant="default">
                <CheckCircle2 size={12} className="mr-1" />
                {t('pages:operations.logViewer.ended', 'Stream ended')}
              </Badge>
            )}
            {connectionState === 'error' && (
              <Badge variant="warning">
                <WifiOff size={12} className="mr-1" />
                {t('pages:operations.logViewer.disconnected', 'Disconnected')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {logs.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => void handleCopy()}>
                {copied ? <Check size={14} className="mr-1" /> : <Clipboard size={14} className="mr-1" />}
                {copied
                  ? t('pages:operations.logViewer.copied', 'Copied!')
                  : t('pages:operations.logViewer.copy', 'Copy')}
              </Button>
            )}
            {connectionState !== 'completed' && (
              <Button variant="ghost" size="sm" onClick={reconnect}>
                <RefreshCw size={14} className="mr-1" />
                {t('pages:operations.logViewer.reconnect', 'Reconnect')}
              </Button>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && connectionState === 'error' && (
          <Alert variant="warning" className="mb-2">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {/* Log output */}
        <div
          ref={scrollRef}
          className="bg-slate-950 rounded-md p-3 font-mono text-xs max-h-96 overflow-y-auto"
        >
          {logs.length === 0 ? (
            <span className="text-slate-500">
              {t('pages:operations.logViewer.waiting', 'Waiting for logs...')}
            </span>
          ) : (
            logs.map((log, index) => {
              const label = levelLabelMap[log.level]
              return (
                <div key={index} className="flex gap-2 py-0.5">
                  <span className="text-slate-500 shrink-0">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  {label.show ? (
                    <Badge variant={label.variant} className="text-[10px] px-1 py-0">
                      {log.level.toUpperCase()}
                    </Badge>
                  ) : (
                    <span className="w-0" />
                  )}
                  <span className={levelColorMap[log.level]}>
                    {log.message}
                  </span>
                </div>
              )
            })
          )}
        </div>
      </div>
    </Card>
  )
}
